import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAudioPlayer } from 'react-use-audio-player';
import kalshiFillSound from 'src/Sounds/kalshi-fill.mp3';
import polyFillSound from 'src/Sounds/poly-fill.mp3';
import alarmSound from 'src/Sounds/error.mp3';
import counterStuckSound from 'src/Sounds/counter_stuck.mp3';
import { isNotificationsRoute } from '../../constants/routes';
import { useNotificationsSocket } from '../../hooks/useNotificationsSocket';
import { useNotificationSoundPlayer } from '../../hooks/useNotificationSound';
import { useSoundLeader } from '../../hooks/useSoundLeader';

/**
 * App-wide prediction-notification SOUND mount.
 *
 * Renders nothing. Mounted ONCE in the predictions sidebar (next to the
 * `NotificationBell`, gated on `mode === 'predictions'`).
 *
 * AUDIBLE ONLY WHEN BOTH HOLD:
 *   1. this tab has `/predictions/notifications` open, and
 *   2. this tab holds the cross-tab sound-leader lock.
 *
 * (1) is the operator's rule: the feed is where the sounds belong, and
 * having it OPEN is the whole condition — the tab may be in the background,
 * minimised or on another monitor and it still rings, because the gate reads
 * `location.pathname`, never `document.visibilityState`. Nothing open on
 * that route anywhere ⇒ nothing rings, which is the intended silence.
 *
 * (2) is `useSoundLeader`: N tabs on the feed ring ONCE for the whole
 * computer. The 2026-05-23 revert of the previous leader election is
 * dissected in that file — the fatal part was releasing the lock on
 * `visibilitychange`, and it is not repeated. The other half of that revert,
 * double-ringing on an HMR remount, cannot happen here either: leadership is
 * a boolean, and there is exactly ONE set of audio players, the one below.
 *
 * 🚨 THE GATE IS ON THE PLAY, NOT ON THE MOUNT. Do not move it up to
 * `SidebarNav` and mount/unmount this component per route: the autoplay
 * priming below happens once per mount, and howler only writes its global
 * clip cache if the howl still has sounds attached when the decode lands.
 * Remounting on every navigation throws that away, so the first real cue
 * after coming back pays a full fetch+decode — and if that fetch fails it is
 * SILENT (howler's `loaderror` is async, so the hook's try/catch never sees
 * it). That is how the naked-leg alarm would go quiet.
 *
 * ⚠️ One caveat on "the feed is open ⇒ this tab can ring": the mount itself is
 * gated on `mode === 'predictions'`, which comes from `localStorage`, not from
 * the URL. A tab deep-linked to the feed while the stored mode is `orderbooks`
 * renders the feed and rings nothing — the sidebar would be showing orderbook
 * routes, so it is a visibly odd state, but it is the one place the two gates
 * disagree about what "open" means.
 *
 * Why mounting here does NOT open a second WebSocket: `NotificationBell`
 * already keeps a `useNotificationsSocket()` alive in predictions mode,
 * and `socket.io-react-hook`'s `IoProvider` ref-counts connections per
 * URL — this second `useNotificationsSocket()` reuses the same socket.
 *
 * Autoplay priming: browsers swallow audio before the first user gesture,
 * so the very first real fill/alarm cue could be dropped. We attach a
 * one-time `pointerdown` listener that silently primes all three players
 * (zero-volume load) and removes itself — idempotent, gesture-driven.
 */
export function PredictionNotificationSound(): null {
  const { queueVersion, drain, isConnected } = useNotificationsSocket();
  const onNotificationsRoute = isNotificationsRoute(useLocation().pathname);
  // The leader is the only tab allowed to ring, so a leader whose own feed is
  // dead is silence for the whole computer — a failure mode that did not exist
  // when every tab rang. `feedConnected` does not decide eligibility (the lock
  // is held continuously on purpose); it lets a leader that has been deaf for
  // long enough stand down for a tab that is waiting and healthy.
  const isSoundLeader = useSoundLeader(onNotificationsRoute, {
    feedConnected: isConnected,
  });

  // The socket exposes a QUEUE (no single-slot overwrite), so a same-tick
  // burst delivers every frame — and every frame is played, in order, by
  // an ordinary loop over the drained batch.
  //
  // This used to route the batch through a React state slot, advancing one
  // event per render on a `queueMicrotask` chain. It LOST EVENTS: the
  // whole chain runs before React commits (the scheduler renders on a
  // macrotask), so the intermediate `setState`s coalesced and the hook only
  // ever saw the FIRST and LAST event of the batch. A `counter_stuck` with
  // anything after it in the same burst — a goal that puts several rules
  // into it plus whatever else fired — never rang at all. Every dedupe and
  // cooldown decision already lives inside `play`, so the loop needs no
  // pacing of its own.
  const play = useNotificationSoundPlayer();

  useEffect(() => {
    if (queueVersion === 0) return;
    // ALWAYS drain, even when muted: the queue is per-hook-instance and
    // nothing else empties it, so skipping the drain would grow it for as
    // long as the operator stays off the feed — and then play the whole
    // backlog at once the moment they navigate back.
    const batch = drain();
    if (!onNotificationsRoute || !isSoundLeader) return;
    for (const { event, live } of batch) play(event, { live });
  }, [queueVersion, drain, play, onNotificationsRoute, isSoundLeader]);

  // One-time autoplay unlock. `useAudioPlayer()` returns a fresh wrapper
  // each render but its `load`/`stop` callbacks are stable — destructure
  // them up front and depend only on those (never the wrapper object), per
  // the documented unstable-wrapper gotcha in useNotificationSound.
  //
  // ONE PLAYER PER CLIP — never two `load()` calls on the same player.
  // `createHowl` begins with `destroyHowl` → `howl.unload()`, and howler
  // only writes its module-global `cache[src]` if the howl still has
  // sounds attached when the decode lands. Priming two clips on one
  // player therefore unloads the first mid-fetch, its decode takes the
  // error path, and that clip is never cached — so it pays a full
  // fetch+decode on its first REAL play, which for `counter_stuck` is
  // the naked-leg alarm. (And a failed fetch there is silent: howler's
  // `loaderror` is async, so the hook's try/catch never sees it.)
  const { load: loadKalshi, stop: stopKalshi } = useAudioPlayer();
  const { load: loadPoly, stop: stopPoly } = useAudioPlayer();
  const { load: loadMain, stop: stopMain } = useAudioPlayer();
  const { load: loadCounterStuck, stop: stopCounterStuck } = useAudioPlayer();

  useEffect(() => {
    let primed = false;
    const prime = () => {
      if (primed) return;
      primed = true;
      try {
        loadKalshi(kalshiFillSound, { autoplay: true, initialVolume: 0 });
        loadPoly(polyFillSound, { autoplay: true, initialVolume: 0 });
        loadMain(alarmSound, { autoplay: true, initialVolume: 0 });
        loadCounterStuck(counterStuckSound, {
          autoplay: true,
          initialVolume: 0,
        });
        // Stop immediately — we only needed the gesture-driven load() to
        // unlock the browser audio context, not to actually play.
        stopKalshi();
        stopPoly();
        stopMain();
        stopCounterStuck();
      } catch {
        // Priming is best-effort; a real fill will still attempt to play.
      }
      window.removeEventListener('pointerdown', prime);
    };
    window.addEventListener('pointerdown', prime, { once: true });
    return () => window.removeEventListener('pointerdown', prime);
  }, [
    loadKalshi,
    stopKalshi,
    loadPoly,
    stopPoly,
    loadMain,
    stopMain,
    loadCounterStuck,
    stopCounterStuck,
  ]);

  return null;
}
